// @flow

import {usePersistentState} from 'docs/data';
import {useMemo, useCallback} from 'react';

type PlaylistWatchHistory = Array<{
  topicID: string,
  current: boolean,
  videos: {
    [videoID: string]: {
      secondsWatched: number,
    },
  },
}>;

type AllPlaylistWatchHistory = {
  [playlist: string]: PlaylistWatchHistory,
  __version: number,
};

const LATEST_SCHEMA_VERSION = 0;

type PersistentWatchHistory = {
  isLoadingPlaylistHistory: boolean,
  currentTopicID: string | null,
  watchHistory: PlaylistWatchHistory,
  setCurrentTopic: (string) => void,
  setSecondsWatchedForVideo: (string, string, number) => void,
};

// Add topic to history if it does not exist
const upsertTopic = (history, topicID) => {
  const newHistory = [...history];
  const topicInHistory = newHistory.find((topic) => topic.topicID === topicID);

  if (!topicInHistory) {
    newHistory.push({
      topicID,
      current: false,
      videos: {},
    });
  }

  return newHistory;
};

export const usePersistentWatchHistory = (
  playlist: string,
): PersistentWatchHistory => {
  // Load watch data from IndexedDB. As a future improvement, we can store this data in Mongo.
  const [
    allPlaylistWatchHistory,
    setPlaylistWatchHistory,
    isLoadingPlaylistHistory,
  ] = usePersistentState<AllPlaylistWatchHistory>(`video-playlists`, {
    [playlist]: [],
    __version: 0,
  });

  let history: PlaylistWatchHistory = useMemo(
    () => allPlaylistWatchHistory[playlist] || [],
    [playlist, allPlaylistWatchHistory],
  );
  if (allPlaylistWatchHistory.__version < LATEST_SCHEMA_VERSION) {
    // Handle the scenario where we update the schema but a client has a previous version
    // of their data stored locally. We just clear it for now.
    setPlaylistWatchHistory({
      [playlist]: [],
      __version: LATEST_SCHEMA_VERSION,
    });

    history = [];
  }

  const currentTopic = history.filter((topic) => topic.current === true)[0];

  const setCurrentTopic = (topicID: string) => {
    const newHistory = upsertTopic(history, topicID).map((topic) => {
      return {
        ...topic,
        current: topic.topicID === topicID,
      };
    });

    setPlaylistWatchHistory({
      ...allPlaylistWatchHistory,
      [playlist]: newHistory,
    });
  };

  // TODO (playlist-videos): We should find a way to simplify all of this code
  const setSecondsWatchedForVideo = useCallback(
    (videoID: string, topicID: string, secondsWatched: number) => {
      const newHistory = upsertTopic(history, topicID).map((topic) => {
        if (topic.topicID === topicID) {
          const newVideo = topic.videos[videoID] || {};
          const videos = {
            ...topic.videos,
            [videoID]: {
              ...newVideo,
              secondsWatched,
            },
          };

          return {
            ...topic,
            videos,
          };
        } else {
          return topic;
        }
      });

      setPlaylistWatchHistory({
        ...allPlaylistWatchHistory,
        [playlist]: newHistory,
      });
    },
    [allPlaylistWatchHistory, history, playlist, setPlaylistWatchHistory],
  );

  return {
    isLoadingPlaylistHistory,
    currentTopicID: currentTopic ? currentTopic.topicID : null,
    watchHistory: history,
    setCurrentTopic,
    setSecondsWatchedForVideo,
  };
};
